/**
 * Productivity Metrics Hook
 * React hook for accessing productivity metrics and ROI calculations
 */

import { useCallback, useState, useEffect } from 'react';
import { productivityMetrics, ProductivityMetrics, ProductivityConfig } from '../services/productivityMetrics';
import { useAuth } from '../context/AuthContext';

export const useProductivityMetrics = () => {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState<ProductivityMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const loadMetrics = useCallback(async () => {
    try {
      setIsLoading(true);
      const userId = user?.email || user?.id;
      const productivityData = await productivityMetrics.getProductivityMetrics(userId);
      setMetrics(productivityData);
    } catch (error) {
      console.error('Failed to load productivity metrics:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  const getQuickSummary = useCallback(async () => {
    const userId = user?.email || user?.id;
    return await productivityMetrics.getQuickSummary(userId);
  }, [user]);

  const updateConfig = useCallback((updates: Partial<ProductivityConfig>) => {
    productivityMetrics.updateConfig(updates);
    // Reload metrics after config update
    setTimeout(() => loadMetrics(), 100);
  }, [loadMetrics]);

  // Auto-load metrics when user changes
  useEffect(() => {
    loadMetrics();
  }, [loadMetrics]);

  return {
    metrics,
    isLoading,
    loadMetrics,
    getQuickSummary,
    updateConfig
  };
}; 